import { MapPin, Calendar, Euro, Link, File, Clock } from "lucide-react";
import { AccommodationActivity, ItineraryActivity } from "../models/activity.model";
import { BaseActivityCard } from "./BaseActivityCard";
import { DocumentationList } from "@/components/ui/documentation-list";
import { MediaCarousel } from "@/components/ui/MediaCarousel";

interface AccommodationCardProps {
  activity: AccommodationActivity;
  onEdit: (activity: ItineraryActivity) => void;
  onDelete: (activity: ItineraryActivity) => void;
  onDuplicate?: (activity: ItineraryActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
  onDragStart?: (activity: ItineraryActivity) => void;
  onDropBefore?: (sourceActivityId: string, targetActivityId?: string) => void;
  onDragEnd?: () => void;
  isTraveler?: boolean;
  isAdmin?: boolean;
}

export const AccommodationCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onExpand,
  isReadOnly = false,
  onDragStart,
  onDropBefore,
  onDragEnd,
  isTraveler = false,
  isAdmin = false
}: AccommodationCardProps) => {
  const isInActivity = activity.inOrOutActivity === 'in';
  const isOutActivity = activity.inOrOutActivity === 'out';

  // Helper function to format date range
  const formatDateRange = (start_date: string, end_date: string) => {
    const start = new Date(start_date);
    const end = new Date(end_date);

    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endDay = end.getDate();

    return `${startMonth} ${startDay} - ${endDay}`;
  };

  const formatTime = (time: string) => time.slice(0, 5);
  const renderAlwaysVisible = () => (
    <>
      {activity.address && (
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          <span>{activity.address}</span>
        </div>
      )}
      {activity.start_date && activity.end_date && (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>{formatDateRange(activity.start_date, activity.end_date)}</span>
        </div>
      )}
      {isInActivity && activity.check_in_time && activity.check_in_time !== null && (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>{formatTime(activity.check_in_time)}</span>
        </div>
      )}
      {isOutActivity && activity.check_out_time && activity.check_out_time !== null && (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>{formatTime(activity.check_out_time)}</span>
        </div>
      )}
      {isInActivity && activity.cost && activity.cost > 0 && (isAdmin || !isTraveler) && (
        <div className="flex items-center gap-2">
          <Euro className="h-4 w-4" />
          <span>{activity.cost}</span>
        </div>
      )}
    </>
  );

  const renderCollapsible = () => (
    <div className="space-y-2">
      {/* Note sempre visibile */}
      {activity.description && (
        <div className="mb-4">
          <p className="text-sm text-gray-600 leading-relaxed">{activity.description}</p>
        </div>
      )}

      {/* Check-in Note - Full width */}
      {activity.check_in_note && (
        <div>
          <span className="text-sm font-medium mb-1 block">Check-in Note</span>
          <p className="text-sm text-gray-600">{activity.check_in_note}</p>
        </div>
      )}

      {/* Media Gallery */}
      <MediaCarousel media={activity.media || []} />

      {/* Documentation */}
      <DocumentationList documentation={activity.documentation || []} />

      {/* Link */}
      {activity.link && (isAdmin || !isTraveler) && (
        <a
          href={activity.link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 mt-2 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <Link className="h-3 w-3 flex-shrink-0" />
          <span className="truncate max-w-[200px]">{activity.link}</span>
        </a>
      )}
    </div>
  );

  return (
    <BaseActivityCard
      activity={activity}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onDragStart={onDragStart}
      onDropBefore={onDropBefore}
      onDragEnd={onDragEnd}
      onExpand={onExpand}
      isReadOnly={isReadOnly}
      isTraveler={isTraveler}
      isAdmin={isAdmin}
    >
      {{
        alwaysVisible: renderAlwaysVisible(),
        collapsible: renderCollapsible()
      }}
    </BaseActivityCard>
  );
};
